<script setup lang="ts">
import PageLabel from "components/common/PageLabel.vue";
import {QuestionFilled} from "@element-plus/icons-vue";
import {computed, onMounted, reactive, ref} from "vue";
import {SectionService} from "@/api/api.ts";
import {MyFileUploader, UploadedCourseFile} from "@/types";
import {getFileSize, getTimeStr} from "@/utils/common.ts";
import AddFilesDialog from "components/dialog/common/innner/AddFilesDialog.vue";
import {uploadFile} from "@/utils/cos.ts";
import {ElNotification} from "element-plus";
import UploadFileListDialog from "@/components/dialog/common/UploadFileListDialog.vue";

const pagination = reactive({
  current: 1,
  pageSize: 10,
  total: 0
})
const content = ref("")
const uploaderList = ref<MyFileUploader[]>()
const uploadListDialog = ref<InstanceType<typeof UploadFileListDialog>>()
const addFilesDialog = ref<InstanceType<typeof AddFilesDialog>>()
const buttonRef = ref()

const loading = ref(false)
const fileList = ref<UploadedCourseFile[]>([])

const onUploadFile = () => {
  addFilesDialog.value?.show()
}

const uploadingSize = computed(() => {
  let count = 0;
  if (uploaderList.value) {
    uploaderList.value.forEach(myUploader => {
      if (myUploader.status === 0) {
        count++
      }
    })
  }
  return count
})

const startUpload = (_uploaderList: MyFileUploader[]) => {
  uploaderList.value = _uploaderList
  uploaderList.value.forEach(uploader => {
    uploadFile(uploader.file, {
      onProgress: (progressData) => {
        uploader.file.progress = Math.ceil(progressData.percent * 100);
      },
      onFileFinish(url: string) {
        uploader.file.url = url
        SectionService.addCourseFile({
          name: uploader.file.name,
          size: uploader.file.size || 0,
          ext: uploader.file.ext || '',
          url: uploader.file.url,
        }).then(res => {
          if (res.data.code === 200) {
            uploader.status = 1
            getUploadedFiles()
          } else {
            ElNotification.error({
              title: "上传文件失败",
              message: res.data.msg,
              type: "error"
            })
            uploader.status = 2
          }
        }).catch(err => {
          console.warn(err)
          uploader.status = 2
        })
      },
      onUploadFail(error: string) {
        console.error(error)
        uploader.status = 2
      },
      onTaskReady(taskId: string) {
        uploader.file.taskId = taskId
      }
    })
  })
  console.log(uploaderList.value)
  addFilesDialog.value?.destroy()
}

const getUploadedFiles = async () => {
  try {
    loading.value = true
    const res = await SectionService.getUploadedFile({
      page: pagination.current,
      pageSize: pagination.pageSize,
      content: content.value
    })
    if (res.data.code === 200) {
      pagination.total = res.data.data?.count || 0
      pagination.current = res.data.data?.page || 1
      fileList.value = res.data.data?.list || []
    }
  } catch (e) {
    console.warn(e)
  } finally {
    loading.value = false
  }
}

const onPreview = (url: string) => {
  window.open(url, '_blank')
}

const showUploadList = () => {
  uploadListDialog.value?.show()
}

getUploadedFiles()

const onPageChange = (page: number) => {
  pagination.current = page
  getUploadedFiles()
}

const onSearch = () => {
  pagination.current = 1
  getUploadedFiles()
}

const onSizeChange = (pageSize: number) => {
  pagination.pageSize = pageSize
  getUploadedFiles()
}

onMounted(() => {
  window.addEventListener("beforeunload", e => {
    if (uploadingSize.value > 0) {
      e.preventDefault();
    }
  })
})
</script>

<template>
  <div class="mx-3rem mt-2.25rem">
    <page-label>
      <div class="flex items-center">
        <span class="mr-1rem">文件列表</span>
        <el-icon ref="buttonRef" class="cursor-pointer">
          <QuestionFilled/>
        </el-icon>
        <el-popover
            ref="popoverRef"
            :virtual-ref="buttonRef"
            trigger="click"
            title=""
            width="16rem"
            virtual-triggering
        >
          <div>提前上传文件，在批量上传章节时，添加课件可以直接使用文件id。</div>
        </el-popover>
      </div>
    </page-label>
    <div class="p-1rem bg-white rounded-md mt-2rem shadow-md gap-1rem">
      <div class="relative flex items-center">
        <el-button type="primary" @click="onUploadFile">上传文件</el-button>
        <el-input v-model="content" placeholder="搜索文件" class="ml-1rem" style="width: 20rem" @change="onSearch"></el-input>
        <el-button type="primary" @click="onSearch" class="ml-1rem">查询</el-button>
        <div class="ml-2rem absolute right-0">
          <span class="mr-.5rem text-gray-400">{{ `${uploadingSize}个文件上传中` }}</span>
          <el-button type="primary" @click="showUploadList">查看上传队列</el-button>
        </div>
      </div>
    </div>
    <div class="p-1rem bg-white rounded-md mt-1rem shadow-md" v-loading="loading">
      <el-table :data="fileList">
        <el-table-column prop="id" label="ID">
        </el-table-column>
        <el-table-column prop="name" label="文件名称">
        </el-table-column>
        <el-table-column prop="size" label="文件大小">
          <template #default="scope">
            {{ getFileSize(scope.row.size) }}
          </template>
        </el-table-column>
        <el-table-column prop="duration" label="类型">
          <template #default="scope">
            {{ scope.row.ext }}
          </template>
        </el-table-column>
        <el-table-column label="创建时间">
          <template #default="scope">
            {{ getTimeStr(scope.row.create_time * 1000) }}
          </template>
        </el-table-column>
        <el-table-column label="操作">
          <template #default="scope">
            <el-button type="danger" @click="onPreview(scope.row.url)">预览</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
          class="mt-1rem"
          background
          :page-sizes="[10, 20, 50, 100]"
          layout="prev, pager, next, jumper, ->, total, sizes"
          :current-page="pagination.current"
          :page-size="pagination.pageSize"
          :total="pagination.total"
          @current-change="onPageChange"
          @size-change="onSizeChange"/>
    </div>
  </div>
  <add-files-dialog ref="addFilesDialog" @on-start-upload="startUpload"></add-files-dialog>
  <upload-file-list-dialog ref="uploadListDialog" :uploader-list="uploaderList" />
</template>

<style scoped>
</style>
